// Smart Sash Admin Panel - User Scope Management Page

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, PermissionGate, Spinner } from '@/components/common';
import { DeviceSelector } from '@/components/scope/DeviceSelector';
import { useDirtyStateWarning } from '@/hooks';
import { useUserScopeManagement } from '@/hooks/useUserScopeManagement';

export const UserScopeManagement: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    roles,
    selectedRoleId,
    isLoading,
    isSaving,
    error,
    isDirty,
    selectorData,
    initialSelectedIds,
    handleRoleChange,
    handleSelectionChange,
    handleSave,
    clearError,
  } = useUserScopeManagement();

  useDirtyStateWarning(isDirty);

  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User'
    : 'Loading...';

  if (isLoading) {
    return (
      <div className="container-fluid py-4 text-center">
        <Spinner />
        <p className="text-muted mt-2">Loading user scopes...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button
                variant="secondary"
                size="sm"
                icon="fas fa-arrow-left"
                onClick={() => navigate('/users')}
                className="mb-2"
              >
                Back to Users
              </Button>
              <h1 className="h3 mb-0">User Assignments: {userName}</h1>
              <p className="text-muted mb-0">
                Choose a role, then select the devices this role can access. Complete lab or group
                selections are saved as a single parent assignment.
              </p>
            </div>
            <PermissionGate action="scope:edit">
              <Button
                variant="primary"
                icon="fas fa-save"
                onClick={handleSave}
                disabled={!isDirty || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {error && (
        <div className="row mb-3">
          <div className="col-12">
            <Alert variant="danger" dismissible onDismiss={clearError}>
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
            </Alert>
          </div>
        </div>
      )}

      {isDirty && (
        <div className="row mb-3">
          <div className="col-12">
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              You have unsaved changes.
            </Alert>
          </div>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-6 col-lg-4">
          <label className="form-label" htmlFor="assignment-role">
            Assignment role
          </label>
          <select
            id="assignment-role"
            className="form-select"
            value={selectedRoleId}
            disabled={roles.length === 0 || isSaving || isDirty}
            onChange={(event) => handleRoleChange(event.target.value)}
          >
            {roles.length === 0 && <option value="">No roles available</option>}
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name || role.id}
              </option>
            ))}
          </select>
          {isDirty && <div className="form-text">Save changes before switching roles.</div>}
        </div>
      </div>

      {selectedRoleId ? (
        <DeviceSelector
          data={selectorData}
          initialSelectedIds={initialSelectedIds}
          onSelectionChange={handleSelectionChange}
        />
      ) : (
        <Alert variant="warning">No role is available for creating assignments.</Alert>
      )}
    </div>
  );
};

export default UserScopeManagement;
